import ElectronIPC from '@/services/ElectronIPC'
import { env } from '@/services/EnvironmentVariables'
import { sleep } from '@/utils'
import _ from 'lodash'
import { useEffect, useState } from 'react'
import { TypedEmitter } from 'tiny-typed-emitter'

export type ViewMode = {
    galleryLayout: GalleryLayout

    videoOrder: string[]

    // Indicates that the gallery should not be rendered. When sidebar is popped out, or for mobile.
    galleryHidden: boolean
    wasGalleryHidden: boolean

    fullscreen: boolean

    // Indicates if the sidebar has been popped out into a new window.
    // Typically, when the app is fullscreened.
    sidebarPoppedOut: boolean

    // The document of the sidebar popped out window. This is set when the
    // pop out process is complete and elements are good to be measured.
    sidebarPoppedOutDocument: Document | null

    // Inidates if the sidebar is collapsed/retracted into the "thin strip" mode
    sidebarCollapsed: boolean

    // Indicates the gallery is busy, and videos should be rendered in the sidebar instead
    showVideosInSidebar: boolean

    // Prevents any videos from rendering in the sidebar
    sidebarVideosHidden: boolean

    // Indicates if the muse window is pinned to be always on top of otehr windows
    alwaysOnTop: boolean
    wasAlwaysOnTopBeforeGalleryHidden: boolean
}

export type GalleryLayout =
    | { name: 'none' | 'invite' | 'speaker' | 'speaker-and-grid' }
    | { name: 'grid'; page: number; reordering: boolean }
    | {
          name: 'pinned' | 'pinned-and-speaker-pnp'
          pinnedVideo: PinnedVideoInfo
          page: number
      }
    | { name: 'test-grid'; page: number; numVideos: number }

// NOTE: In the future this might need to be more precise to support users
// sharing multiple cameras or screens.
export type PinnedVideoInfo = {
    peerSessionId: string
    kind: 'camera' | 'screen'
}

type ModeStateEvents = {
    change: (mode: ViewMode) => void
    videoOrderChange: (videoOrder: string[]) => void
}
class ViewModeState extends TypedEmitter<ModeStateEvents> {
    mode: ViewMode = {
        galleryLayout: { name: 'invite' },
        videoOrder: [],
        galleryHidden: false,
        wasGalleryHidden: false,
        fullscreen: false,
        sidebarVideosHidden: false,
        sidebarPoppedOut: false,
        sidebarPoppedOutDocument: null,
        alwaysOnTop: false,
        wasAlwaysOnTopBeforeGalleryHidden: false,

        sidebarCollapsed: false,
        showVideosInSidebar: true,
    }

    constructor() {
        super()
        this.setMaxListeners(100)
    }

    public setGalleryLayout(layout: GalleryLayout) {
        this.mode.galleryLayout = layout
        this.mode.showVideosInSidebar = calcShowVideosInSidebar(layout, this.mode.galleryHidden)
        this.emit('change', this.mode)
    }

    public setVideoOrder(videoOrder: string[]) {
        this.mode.videoOrder = videoOrder
        this.emit('videoOrderChange', this.mode.videoOrder)
    }

    public toggleReorderVideoMode() {
        if (this.mode.galleryLayout.name === 'grid') {
            this.mode.galleryLayout.reordering = !this.mode.galleryLayout.reordering
            this.emit('change', this.mode)
        }
    }

    public moveVideo(from: number, to: number) {
        if (this.mode.galleryLayout.name !== 'grid') {
            return
        }

        const [movedItem] = this.mode.videoOrder.splice(from, 1)
        this.mode.videoOrder.splice(to, 0, movedItem)

        this.emit('videoOrderChange', this.mode.videoOrder)
    }

    public async enterFullscreen(source: 'internal' | 'external') {
        if (this.mode.fullscreen) {
            return
        }

        this.mode.fullscreen = true
        this.mode.wasGalleryHidden = this.mode.galleryHidden
        this.mode.galleryHidden = false
        if (env.IS_ELECTRON) {
            this.mode.sidebarPoppedOut = true
        }
        this.emit('change', this.mode)

        if (source === 'internal') {
            if (env.OS === 'darwin' && env.IS_ELECTRON) {
                // Exit fullscreen that might have been there.
                // The point is that, if native fullscreen is already on, commanding another fullscreen
                // on top results in strange window behavior (Mac). This is rarely a problem since native
                // fullscreen gets picked up by the external event module and triggers enterFullscreen('os')
                // and internal `enterFullscreen('internal')` double-spend is restricted programmatically.
                ElectronIPC.setWindowBounds({
                    isFullscreen: false,
                })

                await sleep(100)
            }

            if (env.IS_ELECTRON) {
                // if app not focused, then trigger focus (fixes #985)
                const windowState = await ElectronIPC.getWindowState({ windowName: 'mainWindow' })
                if (!windowState?.isFocused) {
                    ElectronIPC.setWindowOptions({ triggerFocus: true })
                    await sleep(1000)
                }
            }

            // Enter fullscreen mode
            if (env.IS_ELECTRON) {
                ElectronIPC.setWindowBounds({
                    isFullscreen: true,
                })
            } else {
                document.body.requestFullscreen()
            }
        }
    }

    public async exitFullscreen() {
        if (!this.mode.fullscreen) {
            return
        }

        if (env.IS_ELECTRON) {
            ElectronIPC.setWindowBounds({
                isFullscreen: false,
            })
        } else {
            document.exitFullscreen()
        }
        // setGalleryHidden() needs the `mode.fullscreen` value before it was modified.
        this.setGalleryHidden(this.mode.wasGalleryHidden, true)
        this.mode.fullscreen = false
        this.mode.sidebarPoppedOut = false
        this.mode.sidebarPoppedOutDocument = null
        this.mode.sidebarCollapsed = false
        this.emit('change', this.mode)
    }

    public toggleFullscreen() {
        if (this.mode.fullscreen) {
            this.exitFullscreen()
        } else {
            this.enterFullscreen('internal')
        }
    }

    public setSidebarCollapsed(collapsed: boolean) {
        this.mode.sidebarCollapsed = collapsed
        this.emit('change', this.mode)
    }

    public setGalleryHidden(hidden: boolean, noEmit?: boolean) {
        if (hidden === this.mode.galleryHidden) {
            return
        }
        this.mode.galleryHidden = hidden
        this.mode.showVideosInSidebar = calcShowVideosInSidebar(this.mode.galleryLayout, hidden)

        if (hidden && !this.mode.fullscreen) {
            this.mode.wasAlwaysOnTopBeforeGalleryHidden = this.mode.alwaysOnTop
        }

        if (hidden) {
            this.mode.alwaysOnTop = true
            ElectronIPC.setWindowOptions({ isAlwaysOnTop: true })
        } else {
            if (!this.mode.wasAlwaysOnTopBeforeGalleryHidden) {
                this.mode.alwaysOnTop = false
                ElectronIPC.setWindowOptions({ isAlwaysOnTop: false })
            }
        }

        if (env.STICKY_WINDOW_WHEN_GALLERY_RETRACTED) {
            if (this.mode.galleryHidden && !this.mode.alwaysOnTop) {
                ElectronIPC.setWindowOptions({ isAlwaysOnTop: true })
            } else if (!this.mode.galleryHidden && !this.mode.alwaysOnTop) {
                ElectronIPC.setWindowOptions({ isAlwaysOnTop: false })
            }
        }

        if (!noEmit) {
            this.emit('change', this.mode)
        }
    }

    public setSidebarVideosHidden(hidden: boolean) {
        this.mode.sidebarVideosHidden = hidden
        this.emit('change', this.mode)
    }

    public setAlwaysOnTop(alwaysOnTop: boolean) {
        this.mode.alwaysOnTop = alwaysOnTop
        ElectronIPC.setWindowOptions({ isAlwaysOnTop: alwaysOnTop })
        this.emit('change', this.mode)
    }

    public setSidebarPoppedOutDocument(document: Document) {
        this.mode.sidebarPoppedOutDocument = document
        this.emit('change', this.mode)
    }

    public hasPin(): boolean {
        const n = this.mode.galleryLayout.name
        return n === 'pinned' || n === 'pinned-and-speaker-pnp'
    }

    public pinKind(): 'camera' | 'screen' | null {
        const n = this.mode.galleryLayout.name
        if (n === 'pinned' || n === 'pinned-and-speaker-pnp') {
            return this.mode.galleryLayout.pinnedVideo.kind
        }
        return null
    }

    public pinPeerId(): string | null {
        const n = this.mode.galleryLayout.name
        if (n === 'pinned' || n === 'pinned-and-speaker-pnp') {
            return this.mode.galleryLayout.pinnedVideo.peerSessionId
        }
        return null
    }

    public galleryPageGoto(page: number): void {
        const n = this.mode.galleryLayout.name
        if (n === 'grid' || n === 'test-grid') {
            if (page >= 0 && page < 999 /* TODO: max page */) {
                this.setGalleryLayout({ ...this.mode.galleryLayout, page })
                this.emit('change', this.mode)
            }
        }
    }

    public galleryPageNext(): void {
        const n = this.mode.galleryLayout.name
        if (n === 'grid' || n === 'test-grid') {
            const pg = (this.mode.galleryLayout.page ?? 0) + 1
            this.galleryPageGoto(pg)
        }
    }

    public galleryPagePrev(): void {
        const n = this.mode.galleryLayout.name
        if (n === 'grid' || n === 'test-grid') {
            const pg = (this.mode.galleryLayout.page ?? 0) - 1
            this.galleryPageGoto(pg)
        }
    }
    public get currentGalleryPage(): number {
        return getCurrentGalleryPage(this.mode)
    }
}

export const sessionViewModeState = new ViewModeState()
// @ts-ignore for debugging
window.sessionViewModeState = sessionViewModeState

export function useViewMode(): ViewMode {
    const [mode, setMode] = useState(sessionViewModeState.mode)
    useEffect(() => {
        const h = (newMode: ViewMode) => setMode(_.clone(newMode))
        sessionViewModeState.on('change', h)

        return () => {
            sessionViewModeState.off('change', h)
        }
    }, [])

    return mode
}

export function useVideoOrder(): string[] {
    const [order, setOrder] = useState(sessionViewModeState.mode.videoOrder)
    useEffect(() => {
        const h = () => {
            setOrder(_.clone(sessionViewModeState.mode.videoOrder))
        }
        sessionViewModeState.on('videoOrderChange', h)
        return () => {
            sessionViewModeState.off('videoOrderChange', h)
        }
    }, [])

    return order
}

function calcShowVideosInSidebar(galleryMode: GalleryLayout, galleryHidden: boolean): boolean {
    if (galleryHidden) {
        return true
    }
    switch (galleryMode.name) {
        case 'invite':
        case 'pinned':
        case 'pinned-and-speaker-pnp':
        case 'speaker':
            return true
        case 'grid':
        case 'speaker-and-grid':
        case 'none':
        case 'test-grid':
            return false
    }
}

export function isThinStripSidebar(mode: ViewMode): boolean {
    return (mode.sidebarCollapsed && mode.galleryHidden) || (mode.sidebarCollapsed && mode.sidebarPoppedOut)
}

export function hasPinnedPresentation(mode: ViewMode): boolean {
    return mode.galleryLayout.name === 'pinned' && mode.galleryLayout.pinnedVideo.kind === 'screen'
}

export function getPinInfo(mode: ViewMode): PinnedVideoInfo | null {
    const n = mode.galleryLayout.name
    if (n === 'pinned' || n === 'pinned-and-speaker-pnp') {
        return mode.galleryLayout.pinnedVideo
    }
    return null
}

export function getCurrentGalleryPage(mode: ViewMode): number {
    const n = mode.galleryLayout.name
    if (n === 'grid' || n === 'test-grid' || n === 'pinned' || n === 'pinned-and-speaker-pnp') {
        return mode.galleryLayout.page
    }
    return 0
}

export function isReordering(mode: ViewMode): boolean {
    return mode.galleryLayout.name === 'grid' && mode.galleryLayout.reordering
}
